import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { SharepointConnection } from "@/types/sharepointConnection"
import { classNames } from "@/utils/classNames"
import { IconCloud, IconCloudOff, IconLoader2 } from "@tabler/icons-react"

interface SharepointStatusIndicatorProps {
  connection?: SharepointConnection
  syncingCount: number
  importedFilesCount: number // Total SharePoint files imported
  onManageClick: () => void
  compact?: boolean
}

export const SharepointStatusIndicator: React.FC<
  SharepointStatusIndicatorProps
> = ({
  connection,
  syncingCount,
  importedFilesCount,
  onManageClick,
  compact = false,
}) => {
  const { translate } = useTranslation()

  // Check for manual sync (syncingCount) or auto-sync (connection.isSyncing)
  const isManualSyncing = syncingCount > 0
  const isAutoSyncing = connection?.isSyncing || false
  const isSyncing = isManualSyncing || isAutoSyncing

  let iconBg = "bg-success/10"
  let iconColor = "text-success"
  let statusText = translate("SHAREPOINT/STATUS_CONNECTED")
  let detailText: string
  let isError = false

  if (connection) {
    const isExpired =
      connection.status === "expired" || connection.status === "revoked"
    const failedCount = connection.folderSelections.filter(
      (s) => s.syncStatus === "failed",
    ).length

    // Always show imported files count (not folder count)
    detailText = translate("SHAREPOINT/IMPORT_FILES_IMPORTED", {
      count: importedFilesCount,
    })

    if (isExpired) {
      iconBg = "bg-red1/10"
      iconColor = "text-red1"
      statusText = translate("SHAREPOINT/STATUS_RECONNECT_REQUIRED")
      detailText = translate("SHAREPOINT/STATUS_CONNECTION_EXPIRED")
      isError = true
    } else if (failedCount > 0) {
      iconBg = "bg-red1/10"
      iconColor = "text-red1"
      detailText = translate("SHAREPOINT/STATUS_FOLDERS_FAILED", {
        count: failedCount.toString(),
      })
      isError = true
    }
  } else {
    iconBg = "bg-yellow1/10"
    iconColor = "text-yellow1"
    detailText = translate("SHAREPOINT/STATUS_SELECT_FOLDERS")
  }

  if (isSyncing) {
    iconBg = "bg-yellow1/10"
    iconColor = "text-yellow1"
    if (isAutoSyncing) {
      statusText = translate("SHAREPOINT/STATUS_AUTO_SYNCING")
      detailText = translate("SHAREPOINT/STATUS_CHECKING_UPDATES")
    } else {
      statusText = translate("SHAREPOINT/STATUS_SYNCING")
      detailText = translate("SHAREPOINT/FILES_SYNCING", {
        count: syncingCount,
      })
    }
  }

  if (compact) {
    return (
      <button
        onClick={onManageClick}
        className="flex h-8 items-center gap-1.5 rounded border border-grey4 px-2 text-xs text-grey5 transition-colors hover:border-yellow1 hover:text-yellow1"
        title={`${statusText} · ${detailText}`}
      >
        <div
          className={classNames(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded",
            iconBg,
          )}
        >
          {isSyncing ? (
            <IconLoader2
              size={12}
              className={classNames("animate-spin", iconColor)}
            />
          ) : isError ? (
            <IconCloudOff size={12} className={iconColor} />
          ) : (
            <IconCloud size={12} className={iconColor} />
          )}
        </div>
        <span>{statusText}</span>
        <span className="text-grey4">·</span>
        <span>{detailText}</span>
      </button>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-grey3 bg-grey2 p-3">
      <div
        className={classNames(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          iconBg,
        )}
      >
        {isSyncing ? (
          <IconLoader2
            size={18}
            className={classNames("animate-spin", iconColor)}
          />
        ) : isError ? (
          <IconCloudOff size={18} className={iconColor} />
        ) : (
          <IconCloud size={18} className={iconColor} />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <Typography
          variant="textXS"
          as="span"
          className="font-medium text-white"
        >
          {statusText}
        </Typography>
        <Typography variant="textXS" as="span" className="text-grey5">
          {detailText}
        </Typography>
      </div>
      <button
        onClick={onManageClick}
        className="shrink-0 rounded border border-grey4 px-3 py-1.5 text-xs text-grey5 transition-colors hover:border-yellow1 hover:text-yellow1"
      >
        {translate("SHAREPOINT/MANAGE_BUTTON")}
      </button>
    </div>
  )
}
